import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "template-links d-flex justify-center" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = ["innerHTML"]

import AppBottomBar from '@/components/AppBottomBar.vue';
import SearchInput from '@/components/ui/SearchInput.vue';
import DataTableHeader from '@/components/chunks/DataTableHeader.vue';
import { baseUrl } from '@/backend/Api';
import { useClientStore } from '@/store/client';
import { ClientRowError, DropzoneOptions } from '@/types';
import ApiBodyResponseStatus from '@/types/AppApiResponseStatus';
import Client from '@/types/interfaces/Client';
import { DEFAULT_PAGINATION_PAGE, PAGINATION_TABLES_NAMES, PROVIDE_KEYS } from '@/utils/constants';
import { getAccessToken } from '@/utils/functions';
import axios from 'axios';
import { inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import useTablePaginationMixin from '@/mixins/tablePaginationMixin';
import ClientRowErrorVariant from '@/types/ClientRowErrorVariant';
import ClientRowErrorType from '@/types/ClientRowErrorType';

enum ClientRowField {
  FIRST_NAME = 'FirstName',
  LAST_NAME = 'LastName',
  EMAIL = 'Email',
  PHONE = 'Phone',
  TELEGUARD_ID = 'TeleguardId'
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GroupClients',
  setup(__props) {

const TEMPLATE = {
  FILENAME: 'template',
  EXT: {
    CSV: '.csv',
    XML: '.xml'
  }
};

const { t } = useI18n();
const { showSnackbar }: any = inject(PROVIDE_KEYS.SNACKBAR);
const clientStore = useClientStore();
const router = useRouter();
const { handleUpdatePage } = useTablePaginationMixin();

let parsedClients = ref([] as Client[]);

const localData = ref({
  hasError: false,
  fileServerId: '',
  search: '',
  submitProcess: false,
  headers: [
    {
      title: t('TextField.FirstName'),
      sortable: false,
      key: 'firstName'
    },
    {
      title: t('TextField.LastName'),
      sortable: false,
      key: 'lastName'
    },
    {
      title: t('TextField.Email'),
      sortable: false,
      key: 'email'
    },
    {
      title: t('TextField.Phone'),
      sortable: false,
      key: 'phone'
    },
    {
      title: t('TextField.TeleguardId'),
      sortable: false,
      key: 'teleguardId'
    }
  ]
});

const dropOptions: DropzoneOptions = {
  url: `${baseUrl}/clients/file/upload`,
  headers: {
    Authorization: 'Bearer ' + getAccessToken()
  },
  maxFileSize: 1048576, //1MB
  maxFiles: 1,
  acceptedFiles: ['csv', 'xml']
};

function onFileRemove() {
  parsedClients.value = [];
  localData.value.hasError = false;
  localData.value.fileServerId = '';
  localData.value.search = '';
}

function sending(data: (File & Record<string, string>)[], xhr: XMLHttpRequest) {
  const callback = (e: string) => {
    const sendingResponse = JSON.parse(e);
    if (sendingResponse.responseStatus == ApiBodyResponseStatus.OK) {
      data[0].serverId = sendingResponse.data.serverFilename;
      localData.value.fileServerId = sendingResponse.data.serverFilename;
      parsedClients.value = sendingResponse.data.checkedRows;
      const errorItems = parsedClients.value.filter((item) => Object.keys(item.errorFields!).length > 0);
      localData.value.hasError = errorItems.length > 0;
    } else if (sendingResponse.responseStatus == ApiBodyResponseStatus.BAD_REQUEST) {
      showSnackbar('ResponseMessage.Client.Create.ExceedingLimitError');
    } else if (sendingResponse.responseStatus == ApiBodyResponseStatus.INTERNAL_ERROR) {
      showSnackbar('ResponseMessage.Client.Create.FileParseError');
    }
  };

  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4) {
      callback(xhr.response);
    }
  };
}

function generateTooltipText(errorFields: Record<string, ClientRowError[]>) {
  let message = [];
  for (let errorField in errorFields) {
    const errors = errorFields[errorField] ?? [];
    for (const errorRow of errors) {
      switch (errorRow.error) {
          case ClientRowErrorVariant.EMPTY:
            message.push(t('TextFieldError.Required', { field: t(`TextField.${errorField}`) }));
            break;
          case ClientRowErrorVariant.BAD_FORMAT:
            if (errorField == ClientRowField.PHONE)
              message.push(t('TextFieldError.OnlyClearPhoneNumber', { field: t(`TextField.${errorField}`) }));
            if (errorField == ClientRowField.EMAIL)
              message.push(t('TextFieldError.Email', { field: t(`TextField.${errorField}`) }));
            if (errorField == ClientRowField.TELEGUARD_ID)
              message.push(t('TextFieldError.CustomTeleguardId', { field: t(`TextField.${errorField}`) }));
            break;
          case ClientRowErrorVariant.ALREADY_EXIST:
            message.push(
              t(
                `ResponseMessage.Client.Create.${
                  errorRow.type === ClientRowErrorType.FILE ? 'DuplicateFileRowFieldError' : 'DuplicateRowFieldError'
                }`,
                { field: t(`TextField.${errorField}`) }
              )
            );
            break;
      }
    }
  }
  return message.join('<br>');
}

function detectedClassTR(errorFields: Record<string, ClientRowError[]>) {
  let className = Object.keys(errorFields).length > 0 ? 'error' : '';

  for (let errorField in errorFields) {
    const errors: ClientRowError[] = errorFields[errorField] ?? [];
    const hasFileError = errors.filter((error) => error.type === ClientRowErrorType.FILE).length > 0;
    if (hasFileError) {
      className = 'file-error';
      break;
    }
  }
  return className;
}

function detectedClassTD(field: string, errorFields: Record<string, ClientRowError[]>) {
  return Object.keys(errorFields)
    .map((f) => f.toLowerCase())
    .includes(field.toLowerCase())
    ? 'field-error'
    : '';
}

function save() {
  if (localData.value.fileServerId.length == 0) return false;

  localData.value.submitProcess = true;

  clientStore
    .createManyClients(localData.value.fileServerId)
    .then(
      () => {
        handleUpdatePage(PAGINATION_TABLES_NAMES.CLIENTS, DEFAULT_PAGINATION_PAGE);
        showSnackbar('ResponseMessage.Client.Create.ManySuccess');
        router.push({ name: 'Clients' });
      },
      () => {
        showSnackbar('ResponseMessage.Client.Create.ManyError');
      }
    )
    .finally(() => (localData.value.submitProcess = false));
}

function downloadTemplate(ext: string) {
  const filename = `${TEMPLATE.FILENAME}${ext}`;
  axios({
    url: `/templates/${filename}`,
    method: 'GET',
    responseType: 'blob'
  }).then((response) => {
    const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
    const fileLink = document.createElement('a');
    fileLink.href = fileUrl;
    fileLink.setAttribute('download', filename);
    document.body.appendChild(fileLink);
    fileLink.click();
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_DropZone = _resolveComponent("DropZone")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "pa-0"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, { class: "pa-4" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_v_btn, {
                  variant: "text",
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (downloadTemplate(TEMPLATE.EXT.XML)), ["prevent"]))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(`${TEMPLATE.FILENAME.concat(TEMPLATE.EXT.XML)}`), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  variant: "text",
                  onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (downloadTemplate(TEMPLATE.EXT.CSV)), ["prevent"]))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(`${TEMPLATE.FILENAME.concat(TEMPLATE.EXT.CSV)}`), 1)
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_DropZone, {
                dropzoneClassName: "group-clients-dropbox",
                maxFiles: dropOptions.maxFiles,
                maxFileSize: dropOptions.maxFileSize,
                url: dropOptions.url,
                headers: dropOptions.headers,
                uploadOnDrop: true,
                multipleUpload: true,
                parallelUpload: 3,
                acceptedFiles: dropOptions.acceptedFiles,
                onRemovedFile: onFileRemove,
                onSending: sending
              }, {
                message: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('Label.DropZone.WelcomeMessage')), 1)
                ]),
                _: 1
              }, 8, ["maxFiles", "maxFileSize", "url", "headers", "acceptedFiles"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { class: "pa-4" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              (_unref(parsedClients).length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(SearchInput, {
                      modelValue: localData.value.search,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localData.value.search) = $event)),
                      class: "mt-4 mb-2"
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_data_table, {
                      search: localData.value.search,
                      headers: localData.value.headers,
                      items: _unref(parsedClients),
                      class: "group-clients-table"
                    }, {
                      headers: _withCtx(({ columns, isSorted, getSortIcon, toggleSort }) => [
                        _createVNode(DataTableHeader, {
                          columns: columns,
                          "is-sorted": isSorted,
                          "get-sort-icon": getSortIcon,
                          "toggle-sort": toggleSort
                        }, null, 8, ["columns", "is-sorted", "get-sort-icon", "toggle-sort"])
                      ]),
                      item: _withCtx(({ item }) => [
                        _createElementVNode("tr", {
                          class: _normalizeClass(detectedClassTR((item as Client).errorFields!))
                        }, [
                          (Object.keys((item as Client).errorFields!).length > 0)
                            ? (_openBlock(), _createBlock(_component_v_tooltip, {
                                key: 0,
                                activator: "parent",
                                location: "top"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", {
                                    innerHTML: generateTooltipText((item as Client).errorFields!)
                                  }, null, 8, _hoisted_3)
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true),
                          _createElementVNode("td", {
                            class: _normalizeClass(detectedClassTD('fisrtName', (item as Client).errorFields!))
                          }, [
                            _createElementVNode("span", null, _toDisplayString((item as Client).firstName), 1)
                          ], 2),
                          _createElementVNode("td", {
                            class: _normalizeClass(detectedClassTD('lastName', (item as Client).errorFields!))
                          }, [
                            _createElementVNode("span", null, _toDisplayString((item as Client).lastName), 1)
                          ], 2),
                          _createElementVNode("td", {
                            class: _normalizeClass(detectedClassTD('email', (item as Client).errorFields!))
                          }, [
                            _createElementVNode("span", null, _toDisplayString((item as Client).email), 1)
                          ], 2),
                          _createElementVNode("td", {
                            class: _normalizeClass(detectedClassTD('phone', (item as Client).errorFields!))
                          }, [
                            _createElementVNode("span", null, _toDisplayString((item as Client).phone), 1)
                          ], 2),
                          _createElementVNode("td", {
                            class: _normalizeClass(detectedClassTD('teleguardId', (item as Client).errorFields!))
                          }, [
                            _createElementVNode("span", null, _toDisplayString((item as Client).teleguardId), 1)
                          ], 2)
                        ], 2)
                      ]),
                      _: 1
                    }, 8, ["search", "headers", "items"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (_unref(parsedClients).length > 0 && !localData.value.hasError)
        ? (_openBlock(), _createBlock(AppBottomBar, {
            key: 0,
            class: "justify-end"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                color: "primary",
                variant: "flat",
                disabled: localData.value.submitProcess,
                loading: localData.value.submitProcess,
                onClick: save
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('Button.Create')), 1)
                ]),
                _: 1
              }, 8, ["disabled", "loading"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})